import React from 'react';
import { Shield, Target, Users, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';
import { BreadcrumbSchema } from '../components/Schema';

const About = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="About Us - TruckAccidentSupport Mission & Values"
        description="Learn about TruckAccidentSupport.com's mission to reduce truck accidents through education, safety awareness, and practical resources for drivers and fleet operators."
        canonical="/about"
        keywords="about truck accident support, trucking safety mission, fleet safety education, driver protection resources"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'About', path: '/about' }
      ]} />
      
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              About <span className="text-gradient">TruckAccidentSupport</span>
            </h1>
            <p className="text-xl text-gray-300">
              Dedicated to reducing accidents and protecting trucking professionals through education and awareness.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 md:p-12 mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                Truck accidents are complex, costly, and often preventable. Our mission is to provide clear, 
                practical, and easy-to-understand information that helps reduce accidents and protect drivers 
                and trucking businesses.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                This website was created as an educational resource focused on accident prevention, driver 
                safety awareness, legal and regulatory education, and risk reduction strategies for trucking operations.
              </p>
            </div>

            {/* Core Focus Areas */}
            <h2 className="text-3xl font-bold text-white mb-8 text-center">What We Focus On</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {[
                {
                  icon: Shield,
                  title: 'Accident Prevention',
                  description: 'Providing strategies and best practices to identify and mitigate risks before they become incidents.'
                },
                {
                  icon: Users,
                  title: 'Driver Safety Awareness',
                  description: 'Educational content that empowers drivers with knowledge to make safer decisions on the road.'
                },
                {
                  icon: BookOpen,
                  title: 'Legal & Regulatory Education',
                  description: 'Clear explanations of compliance requirements and legal protections for drivers and fleet operators.'
                },
                {
                  icon: Target,
                  title: 'Risk Reduction Strategies',
                  description: 'Practical approaches to minimize liability and improve operational safety across your fleet.'
                }
              ].map((item, index) => (
                <Card key={index} className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10 mb-4">
                      <item.icon className="h-6 w-6 text-orange-400" />
                    </div>
                    <CardTitle className="text-white text-xl">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Our Belief */}
            <div className="bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/20 rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">Our Belief</h2>
              <p className="text-xl text-gray-200 leading-relaxed">
                We believe that <span className="text-orange-400 font-semibold">informed drivers</span> and{' '}
                <span className="text-orange-400 font-semibold">well-prepared companies</span> are safer, 
                more resilient, and better protected when incidents occur.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Why This Matters</h2>
            <div className="space-y-6">
              {[
                {
                  title: 'Lives at Stake',
                  content: 'Every truck accident has the potential to cause serious injury or death. Prevention saves lives.'
                },
                {
                  title: 'Financial Impact',
                  content: 'Accidents can cost companies millions in liability, lost revenue, and increased insurance premiums.'
                },
                {
                  title: 'Career Protection',
                  content: 'For drivers, accidents can mean lost income, legal trouble, or the end of a career. Knowledge is protection.'
                },
                {
                  title: 'Industry Reputation',
                  content: 'Safety-focused operations build trust with clients, regulators, and the public.'
                }
              ].map((item, index) => (
                <div key={index} className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-orange-400 mb-2">{item.title}</h3>
                  <p className="text-gray-300">{item.content}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
